// pages/PageCheckout/CartContext.tsx

import React, { createContext, useState, useEffect } from "react";
import { Product } from "data/data";
import * as Sentry from "@sentry/react";
import { normalizeProductImageUrl } from "../../utils/imageUtils";
import { REUSABLE_SPOOL_PRICE, REUSABLE_SPOOL_PRODUCT_ID } from "../../utils/filamentUtils";
import { API_BASE_URL } from "../../config/apiConfig";

// Define the base product fields that we need
interface BaseProductFields {
  id: number;
  name: string;
  price: number;
  category: string;
  description: string;
  images: string[];
  features: any[];
  specs: any;
  stock: number;
  offerPrice?: number | null;
  offerName?: string | null;
  offerStartDate?: string | null;
  offerEndDate?: string | null;
  isRobotics?: boolean;
  image?: string; // Add image field for compatibility
}

// Define types - extend BaseProductFields and override id to support string
export interface CartItem extends Omit<BaseProductFields, 'id' | 'description' | 'images' | 'features' | 'specs' | 'image'> {
  id: number | string; // Allow both number and string IDs
  quantity: number;
  isOfferActive: boolean;
  stock: number;
  isRobotics?: boolean;
  includeSpool?: boolean;
  isSpool?: boolean;
  parentProductId?: string | number;
  parentProductName?: string;
  // Make these optional and allow undefined
  description?: string;
  images?: string[];
  features?: any[];
  specs?: any;
  image?: string;
  offerPrice?: number | null;
  offerName?: string | null;
  offerStartDate?: string | null;
  offerEndDate?: string | null;
}

export interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product, includeSpool?: boolean, spoolProduct?: any) => void;
  removeFromCart: (productId: number | string) => void;
  updateQuantity: (productId: number | string, quantity: number) => void;
  clearCart: () => void;
  canAddToCart: (productId: number, additionalQuantity?: number) => boolean;
  getCartTotal: () => number;
  getItemCount: () => number;
}

// Helper to compute isOfferActive
const isOfferActive = (product: Product): boolean => {
  if (!product.offerPrice || !product.offerStartDate || !product.offerEndDate) {
    return false;
  }
  const now = new Date();
  const start = new Date(product.offerStartDate);
  const end = new Date(product.offerEndDate);
  return product.offerPrice < product.price && now >= start && now <= end;
};

// Cache for spool product to avoid repeated fetching
let cachedSpoolProduct: any = null;
let spoolFetchPromise: Promise<any> | null = null;

// Function to fetch the reusable spool product from the database
const fetchReusableSpool = async (): Promise<any> => {
  // Return cached if available
  if (cachedSpoolProduct) {
    return cachedSpoolProduct;
  }

  // If already fetching, return the existing promise
  if (spoolFetchPromise) {
    return spoolFetchPromise;
  }

  // Start fetching
  spoolFetchPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${REUSABLE_SPOOL_PRODUCT_ID}`);
      
      if (response.ok) {
        const data = await response.json();
        const product = data.product || data;
        
        // Parse images if they're a string
        let images = product.images || [];
        if (typeof images === 'string') {
          try {
            images = JSON.parse(images);
          } catch (e) {
            images = [images];
          }
        }
        
        cachedSpoolProduct = {
          ...product,
          id: product.id || REUSABLE_SPOOL_PRODUCT_ID,
          name: product.name || 'Bambu Lab Reusable Spool',
          price: product.price || REUSABLE_SPOOL_PRICE,
          image: product.image || (images.length > 0 ? images[0] : '/images/reusable-spool.jpg'),
          images: Array.isArray(images) ? images : [images],
          category: product.category || 'accessory',
          description: product.description || 'The Bambu Lab Reusable Spool is a durable, eco-friendly filament spool system designed to work with Bambu\'s filament refills.',
          stock: product.stock || 999,
          features: product.features || [],
          specs: product.specs || {},
        };
        
        return cachedSpoolProduct;
      } else {
        // Fallback to hardcoded values if fetch fails
        console.warn('Failed to fetch spool product, using fallback');
        cachedSpoolProduct = {
          id: REUSABLE_SPOOL_PRODUCT_ID,
          name: 'Bambu Lab Reusable Spool',
          price: REUSABLE_SPOOL_PRICE,
          image: '/images/reusable-spool.jpg',
          images: ['/images/reusable-spool.jpg'],
          category: 'accessory',
          description: 'The Bambu Lab Reusable Spool is a durable, eco-friendly filament spool system designed to work with Bambu\'s filament refills.',
          stock: 999,
          features: [],
          specs: {},
        };
        return cachedSpoolProduct;
      }
    } catch (error) {
      console.error('Error fetching spool product:', error);
      // Fallback to hardcoded values
      cachedSpoolProduct = {
        id: REUSABLE_SPOOL_PRODUCT_ID,
        name: 'Bambu Lab Reusable Spool',
        price: REUSABLE_SPOOL_PRICE,
        image: '/images/reusable-spool.jpg',
        images: ['/images/reusable-spool.jpg'],
        category: 'accessory',
        description: 'The Bambu Lab Reusable Spool is a durable, eco-friendly filament spool system designed to work with Bambu\'s filament refills.',
        stock: 999,
        features: [],
        specs: {},
      };
      return cachedSpoolProduct;
    } finally {
      spoolFetchPromise = null;
    }
  })();

  return spoolFetchPromise;
};

// Create context
export const CartContext = createContext<CartContextType | null>(null);

// Provider
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const parsedCart: CartItem[] = JSON.parse(savedCart);
      return parsedCart.map((item) => ({
        ...item,
        image: item.image || normalizeProductImageUrl(item as any),
        isOfferActive: item.isSpool ? false : isOfferActive(item as any),
      }));
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
    
    const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);
    const totalValue = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    
    Sentry.setContext("cart", {
      item_count: totalItems,
      total_value: totalValue,
      items: cart.map(item => ({ 
        id: item.id, 
        name: item.name, 
        quantity: item.quantity,
        isSpool: item.isSpool || false
      }))
    });
  }, [cart]);

  const canAddToCart = (productId: number, additionalQuantity: number = 1) => {
    const item = cart.find((item) => item.id === productId && !item.isSpool);
    const isRobotics = item?.isRobotics || item?.category?.startsWith('robotics');
    if (isRobotics) return true;
    const productStock = item ? item.stock : 0;
    return !item || item.quantity + additionalQuantity <= productStock;
  };

  const addToCart = async (product: Product, includeSpool: boolean = false, spoolProduct?: any) => {
    Sentry.addBreadcrumb({
      category: "cart",
      message: `Added item to cart: ${product.name}`,
      level: "info",
      data: { productId: product.id, price: product.price, includeSpool }
    });

    const resolvedImage = normalizeProductImageUrl(product);

    // If spool is included and no spool product was passed, fetch it
    let resolvedSpoolProduct = spoolProduct;
    if (includeSpool && !resolvedSpoolProduct) {
      try {
        resolvedSpoolProduct = await fetchReusableSpool();
      } catch (error) {
        console.error('Failed to fetch spool product:', error);
        // Continue with fallback
      }
    }

    setCart((prev) => {
      const isRobotics = product.isRobotics || (product as any).isRobotics || product.category?.startsWith('robotics');
      const productStock = product.stock ?? 0;
      const offerActive = isOfferActive(product);
      
      // Check if product already exists (excluding spool items)
      const existingItemIndex = prev.findIndex(item => 
        item.id === product.id && !item.isSpool
      );
      
      let updatedItems = [...prev];
      
      if (existingItemIndex !== -1) {
        // Update existing product
        const existingItem = updatedItems[existingItemIndex];
        
        // Check stock for non-robotics
        if (!isRobotics && existingItem.quantity >= productStock) {
          return prev;
        }
        
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + 1,
          image: resolvedImage,
          images: product.images || existingItem.images || [],
          offerPrice: product.offerPrice,
          offerName: product.offerName,
          offerStartDate: product.offerStartDate,
          offerEndDate: product.offerEndDate,
          isOfferActive: offerActive,
          stock: productStock,
          isRobotics: isRobotics,
          includeSpool: includeSpool || existingItem.includeSpool,
        };
        
        // Handle spool if included
        if (includeSpool && !existingItem.includeSpool && resolvedSpoolProduct) {
          const spoolItem: CartItem = {
            id: resolvedSpoolProduct.id, // Use the real product ID
            name: resolvedSpoolProduct.name || 'Bambu Lab Reusable Spool',
            price: resolvedSpoolProduct.price || REUSABLE_SPOOL_PRICE,
            quantity: existingItem.quantity + 1,
            isOfferActive: false,
            stock: resolvedSpoolProduct.stock || 999,
            isSpool: true,
            parentProductId: product.id,
            parentProductName: product.name,
            image: resolvedSpoolProduct.image || '/images/reusable-spool.jpg',
            images: resolvedSpoolProduct.images || [],
            category: resolvedSpoolProduct.category || 'accessory',
            description: resolvedSpoolProduct.description || '',
            features: resolvedSpoolProduct.features || [],
            specs: resolvedSpoolProduct.specs || {},
          };
          updatedItems.push(spoolItem);
        } else if (includeSpool && existingItem.includeSpool) {
          // Update existing spool quantity
          const spoolIndex = updatedItems.findIndex(
            item => item.isSpool && item.parentProductId === product.id
          );
          if (spoolIndex !== -1) {
            updatedItems[spoolIndex] = {
              ...updatedItems[spoolIndex],
              quantity: updatedItems[spoolIndex].quantity + 1
            };
          }
        }
      } else {
        // New product
        if (!isRobotics && productStock < 1) {
          return prev;
        }
        
        const newItem: CartItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          category: product.category || '',
          description: product.description || '',
          images: product.images || [],
          features: (product as any).features || [],
          specs: (product as any).specs || {},
          stock: productStock,
          offerPrice: product.offerPrice,
          offerName: product.offerName,
          offerStartDate: product.offerStartDate,
          offerEndDate: product.offerEndDate,
          isRobotics: isRobotics,
          image: resolvedImage,
          quantity: 1,
          isOfferActive: offerActive,
          includeSpool: includeSpool,
        };
        updatedItems.push(newItem);
        
        // Add spool if included
        if (includeSpool && resolvedSpoolProduct) {
          const spoolItem: CartItem = {
            id: resolvedSpoolProduct.id, // Use the real product ID
            name: resolvedSpoolProduct.name || 'Bambu Lab Reusable Spool',
            price: resolvedSpoolProduct.price || REUSABLE_SPOOL_PRICE,
            quantity: 1,
            isOfferActive: false,
            stock: resolvedSpoolProduct.stock || 999,
            isSpool: true,
            parentProductId: product.id,
            parentProductName: product.name,
            image: resolvedSpoolProduct.image || '/images/reusable-spool.jpg',
            images: resolvedSpoolProduct.images || [],
            category: resolvedSpoolProduct.category || 'accessory',
            description: resolvedSpoolProduct.description || '',
            features: resolvedSpoolProduct.features || [],
            specs: resolvedSpoolProduct.specs || {},
          };
          updatedItems.push(spoolItem);
        }
      }
      
      return updatedItems;
    });
  };

  const removeFromCart = (productId: number | string) => {
    Sentry.addBreadcrumb({
      category: "cart",
      message: `Removed item from cart: ${productId}`,
      level: "info",
      data: { productId }
    });
    
    setCart((prev) => {
      // If removing a product, also remove its associated spool
      const item = prev.find(item => item.id === productId);
      if (item && !item.isSpool) {
        return prev.filter(item => 
          item.id !== productId && !(item.isSpool && item.parentProductId === productId)
        );
      }
      return prev.filter((item) => item.id !== productId);
    });
  };

  const updateQuantity = (productId: number | string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setCart((prev) => {
      return prev.map((item) => {
        if (item.id === productId) {
          const isRobotics = item.isRobotics || item.category?.startsWith('robotics');
          const newQuantity = isRobotics 
            ? Math.max(1, quantity) 
            : Math.max(1, Math.min(quantity, item.stock));
          
          return { 
            ...item, 
            quantity: newQuantity
          };
        }
        
        // Update associated spool quantity if parent product quantity changes
        if (item.isSpool && item.parentProductId === productId) {
          const parentItem = prev.find(p => p.id === productId);
          if (parentItem && parentItem.includeSpool) {
            return {
              ...item,
              quantity: quantity
            };
          }
        }
        
        return item;
      });
    });
  };

  const clearCart = () => {
    Sentry.addBreadcrumb({
      category: "cart",
      message: "Cart cleared",
      level: "info"
    });
    setCart([]);
    localStorage.removeItem("cart");
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  const getItemCount = () => {
    return cart.reduce((count, item) => {
      if (item.isSpool) return count;
      return count + item.quantity;
    }, 0);
  };

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart, 
      canAddToCart,
      getCartTotal,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};